import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  Button,
  useTheme,
} from '@mui/material';
import {
  Work,
  Person,
  Refresh,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store/types';
import { fetchMyApplications, clearMyApplicationsError } from '@/store';
import { MyApplicationsTable } from '@/components/common/MyApplicationsTable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`applications-tabpanel-${index}`}
      aria-labelledby={`applications-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

export const MyApplicationsPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const {
    myApplications,
    myApplicationsPagination,
    myApplicationsLoading,
    myApplicationsError
  } = useSelector((state: RootState) => state.applications);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const feedType = activeTab === 0 ? 'job' : 'jobseeker';

  useEffect(() => {
    fetchApplications();
  }, [activeTab, paginationState.pageIndex, paginationState.pageSize]);

  const fetchApplications = () => {
    const params: any = {
      page: paginationState.pageIndex + 1,
      limit: paginationState.pageSize,
      sort: '-appliedAt',
      feedType,
    };

    dispatch(fetchMyApplications({ params }));
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPaginationState({ pageIndex: 0, pageSize: 10 });
  };

  const handleRetry = () => {
    dispatch(clearMyApplicationsError());
    fetchApplications();
  };

  const jobApplications = activeTab === 0 ? myApplications : [];
  const jobSeekerApplications = activeTab === 1 ? myApplications : [];

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
          My Applications
        </Typography> */}
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
        {activeTab === 0 ? 'Applied Jobs' : 'Contacted Job Seekers'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
        {activeTab === 0 ? 'Jobs you have applied for in other companies' : 'Job seeker profiles you viewed and contacted.'}

          {/* View and manage all your job applications and recruiter profile views */}
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 64,
              fontWeight: 500,
            },
          }}
        >
          <Tab
            icon={<Work />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* <span>Job Applications</span> */}
                Applied Jobs
                {/* {activeTab === 0 && myApplicationsPagination && (
                  // <Chip
                  //   label={myApplicationsPagination.total}
                  //   size="small"
                  //   color="primary"
                  //   sx={{ height: 20, fontSize: '0.75rem' }}
                  // />
                )} */}
              </Box>
            }
          />
          <Tab
            icon={<Person />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* <span>Job Seeker Applications</span> */}
                Contacted Job Seekers

                {/* {activeTab === 1 && myApplicationsPagination && (
                  <Chip
                    label={myApplicationsPagination.total}
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                )} */}
              </Box>
            }
          />
        </Tabs>

        {/* Tab Content */}
        <TabPanel value={activeTab} index={0}>
          {myApplicationsError ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {myApplicationsError}
              </Alert>
              <Button
                variant="outlined"
                onClick={handleRetry}
                startIcon={<Refresh />}
              >
                Try Again
              </Button>
            </Box>
          ) : jobApplications.length === 0 && !myApplicationsLoading ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 3,
            }}>
              <Work sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No job applications yet
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                When you apply for jobs, they will appear here.
              </Typography>
            </Box>
          ) : (
            <MyApplicationsTable
              applications={jobApplications}
              loading={myApplicationsLoading}
              totalCount={myApplicationsPagination?.total}
              pagination={paginationState}
              onPaginationChange={setPaginationState}
              feedType="job"
            />
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {myApplicationsError ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {myApplicationsError}
              </Alert>
              <Button
                variant="outlined"
                onClick={handleRetry}
                startIcon={<Refresh />}
              >
                Try Again
              </Button>
            </Box>
          ) : jobSeekerApplications.length === 0 && !myApplicationsLoading ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 3,
            }}>
              <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No connections yet
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                When you connect with job seekers, they will appear here.
              </Typography>
            </Box>
          ) : (
            <MyApplicationsTable
              applications={jobSeekerApplications}
              loading={myApplicationsLoading}
              totalCount={myApplicationsPagination?.total}
              pagination={paginationState}
              onPaginationChange={setPaginationState}
              feedType="jobseeker"
            />
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};
